/**
 * Export bundle (005 T012): builds an ExportBundle from a batch's outputs using
 * buildRef() paths and hands it to PlatformIO (desktop folder tree / web zip).
 * Also produces an export report flagging any missing local files.
 */
import type { OutputRecord, PlatformIO, PrintFileRef } from "@genz/platform";
import { getExportPrintFiles } from "../stores/exportPrintFiles.js";
import { printRefFor } from "./refs.js";

/** Default bundle root for the mockup export flow (mock §Batch → "Mockups/…"). */
export const MOCKUPS_BUNDLE_ROOT = "Mockups";

/** Bundle root for the Transparent tab's keyed overlays (WS-I / D-11), a sibling of
 *  `MOCKUPS_BUNDLE_ROOT` under the SAME operator-chosen save folder. */
export const TRANSPARENT_BUNDLE_ROOT = "Transparent";

export interface ExportReport {
  written: number;
  missing: { ref: string; reason: string }[];
  /**
   * Files the platform tried to write and COULD NOT (disk full, permission denied, path
   * refused). The platform counted these all along (`ExportBundleResult.failed/errors`) but
   * this report dropped the detail, so the operator was told "Saved 8 mockups" for a
   * 10-file bundle with no hint that 2 writes had blown up, let alone why.
   */
  failed: { ref: string; reason: string }[];
  /**
   * Refs that resolved onto a path ANOTHER file in the same bundle had already claimed.
   * They are not written — writing both means the second silently overwrites the first,
   * which is exactly the data loss this bundle exists to prevent.
   */
  collisions: { ref: string; reason: string }[];
  /** True when the user cancelled the destination dialog — nothing was written. */
  cancelled?: boolean;
  /** WHERE the files landed (desktop only). Reported so the UI can show the real destination
   *  and offer to open it, instead of a bare count the operator cannot verify. */
  destPath?: string;
  /** Set when revealing `destPath` in the file manager failed. */
  openError?: string;
}

/** The slice of the app logger this module needs (see `AppLogger` in app/context). Optional
 *  everywhere: a caller without observability wiring still gets the report. */
export interface BundleLogger {
  log(level: string, message: string, ctx?: Record<string, unknown>): void;
}

export interface BundleInput {
  name: string;
  /** Batch render outputs. Optional since WS-I: an overlay export has none, and inventing an
   *  empty array at every call site is how a shared module grows a second shape. */
  outputs?: OutputRecord[];
  /**
   * Pre-pathed files that are not batch outputs — the Transparent tab's keyed overlays (WS-I /
   * D-11). They go through the SAME claim map, the same collision report, the same per-file
   * failure reasons and the same run folder as a mockup export, because "a second export
   * pipeline with its own folder scheme" is exactly what AUD-3 IM-4 filed.
   * Staged AFTER `outputs` and BEFORE `sheet`, so ordering is deterministic.
   */
  files?: { relPath: string; bytes: Uint8Array }[];
  printFiles?: (PrintFileRef & { bytes?: Uint8Array })[];
  /** Optional sheet file to include at the bundle root. */
  sheet?: { name: string; bytes: Uint8Array };
  /** Desktop: write into this pre-chosen folder without prompting (web ignores it). */
  destDir?: string;
  /** Client logger, so write failures + path collisions reach Settings → Observability →
   *  Error log and not just the transient status line under the button. */
  logger?: BundleLogger;
  /** WS-H1 / D-13: the run's dated subfolder (`useBatchStore.runFolder`). Absent = write
   *  straight into `name`, i.e. the pre-WS-H1 overwrite behavior. */
  runFolder?: string;
  /**
   * WS-N review-1 M-5: a RUN-SCOPED claim map, when one logical run reaches this module through
   * several calls.
   *
   * The case-insensitive collision guard below is per call, which is right for a single export.
   * A PAUSED conversion, though, writes its segments through separate calls into ONE run folder:
   * `Apple/Rose.png` from the paused segment and `Apple/rose.png` from the resumed one are one
   * file on APFS/NTFS, so the second call silently destroyed the first — precisely the loss this
   * module exists to prevent, re-opened at the segment boundary. A caller that owns such a run
   * hands the SAME map to every call and the guard spans them.
   *
   * Opt-in on purpose. A re-save of the same rows into the same folder ("Save all", WS-I / D-13)
   * is a deliberate overwrite, and must keep starting from a clean map — so callers that mean
   * "overwrite my own run" simply pass nothing.
   */
  claimed?: Map<string, string>;
}

/**
 * Compose the bundle name the platform is handed: on desktop the run folder NESTS under the
 * root (`writeBundleTo` joins it and `mkdirp`s each parent, so `Mockups/2026-09-11 14-32`
 * needs no change in `desktop.ts`); on web it is appended with a SPACE — a `/` in a download
 * filename is not a folder, it is a broken name.
 */
export function runBundleName(kind: "desktop" | "web", root: string, runFolder?: string): string {
  if (!runFolder) return root;
  return kind === "web" ? `${root} ${runFolder}` : `${root}/${runFolder}`;
}

/** Sniff an encoded image's real format from its magic bytes (png/jpg/webp), or null. */
export function sniffImageExt(bytes?: Uint8Array): "png" | "jpg" | "webp" | null {
  if (!bytes || bytes.length < 12) return null;
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return "png";
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return "jpg";
  if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46
    && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) return "webp";
  return null;
}

/**
 * Derive the per-design PRINT FILES from a batch's rendered outputs — the production
 * artwork, one per design, beside its mock-ups in the model folder (`printRefFor`: the
 * mock-up's view part swapped for "Print", 1:1 with `buildRef`'s mockups scheme).
 *
 * Two sources, in priority order (WS-H2 / D-10):
 *   1. a RENDERED print file from `rendered` — the back print area re-rendered at
 *      `render_settings.print_dpi` by the engine. It carries a real `dpi` and always PNG bytes.
 *   2. otherwise the customer's uploaded artwork — production INPUT, not a render, so it
 *      carries NO dpi. It used to be stamped `dpi: 300` here, which is what made a dead
 *      setting look wired (AUD-3 LB-7).
 *
 * Pass the staged `designs` (batch store) to attach the REAL artwork bytes. Without bytes the ref
 * previously shipped byte-less on every export — an eternally-empty `Print/` folder that
 * read as "files not saving". The extension follows the ACTUAL bytes (magic sniff), never
 * the workspace's mockup format — a PNG design named `.webp` won't open in OS viewers.
 */
export function printFilesFromOutputs(
  outputs: OutputRecord[],
  designs?: readonly { designSlug: string; bytes?: Uint8Array }[],
  rendered?: readonly (PrintFileRef & { bytes?: Uint8Array; templateId?: string })[],
): (PrintFileRef & { bytes?: Uint8Array })[] {
  const bytesBySlug = new Map((designs ?? []).map((d) => [d.designSlug, d.bytes]));
  // WS-H2 / D-10: a RENDERED print file (the back print area at `render_settings.print_dpi`)
  // wins over the customer's uploaded artwork. Keyed the same way the dedup below is, so a
  // multi-template run cannot hand one template's render to another's Print folder.
  const renderedByKey = new Map(
    (rendered ?? []).map((r) => [`${r.templateId ?? ""}::${r.designSlug}`, r]),
  );
  const byDesign = new Map<string, PrintFileRef & { bytes?: Uint8Array }>();
  for (const o of outputs) {
    // Dedup per (TEMPLATE, design), not per design: each template writes into its own folder,
    // so keying on the slug alone meant only the FIRST template's `Print/` folder ever got
    // artwork and every other template shipped an empty Print folder.
    const key = `${o.templateId ?? ""}::${o.designSlug}`;
    if (byDesign.has(key)) continue;
    let ref = printRefFor(o.localRef, o.view);
    const hit = renderedByKey.get(key);
    // review-1 I-1: a rendered print file whose bytes were SPILLED after the auto-save still
    // wins — it is on disk under `savedRoot`, and `hydrateOutputBytes` reads it back from the
    // path derived right here. Gating on bytes alone made a spilled run silently fall back to
    // the customer's 72-DPI upload: the same "found out at the print shop" failure D-10 exists
    // to end, just with an extra step.
    if (hit && ((hit.bytes && hit.bytes.length > 0) || hit.savedRoot)) {
      // The engine always encodes the print file as PNG, so the extension follows the BYTES —
      // a PNG stream named `.jpg` will not open in OS viewers.
      byDesign.set(key, {
        designSlug: o.designSlug,
        view: "back",
        localRef: ref.replace(/\.[a-z0-9]+$/i, ".png"),
        dpi: hit.dpi,
        bytes: hit.bytes,
        ...(hit.savedRoot ? { savedRoot: hit.savedRoot } : {}),
      });
      continue;
    }
    const bytes = bytesBySlug.get(o.designSlug);
    const sniffed = sniffImageExt(bytes);
    if (sniffed) ref = ref.replace(/\.[a-z0-9]+$/i, `.${sniffed}`);
    byDesign.set(key, { designSlug: o.designSlug, view: "back", localRef: ref, bytes });
  }
  return [...byDesign.values()];
}

// macOS privacy (TCC) refuses Desktop/Documents/Downloads with EPERM, and an ad-hoc signed
// update loses the earlier grant — the reason alone gives the operator nothing to act on.
function privacyHint(reason: string): string {
  return /operation not permitted|os error 1\b/i.test(reason)
    ? ". macOS blocked this folder: allow Image Maker Gen Z in System Settings → Privacy & Security → Files & Folders, or choose a folder outside Desktop, Documents and Downloads."
    : "";
}

/**
 * The operator-facing tail for a save/export status line: the REASON files didn't land.
 * Empty string when everything wrote. Shared by every export surface so a disk-full or a
 * path collision reads the same wherever it happens — "N failed" with no reason is not an
 * error report, it's a rumour.
 */
export function exportProblemSummary(report: ExportReport): string {
  const parts: string[] = [];
  if (report.failed.length > 0) {
    parts.push(
      `${report.failed.length} file${report.failed.length === 1 ? "" : "s"} could not be written — ` +
        `${report.failed[0].ref}: ${report.failed[0].reason}` +
        (report.failed.length > 1 ? ` (+${report.failed.length - 1} more in Settings → Observability → Error log)` : "") +
        privacyHint(report.failed[0].reason),
    );
  }
  if (report.collisions.length > 0) {
    parts.push(
      `${report.collisions.length} output${report.collisions.length === 1 ? "" : "s"} skipped — same file path as another design ` +
        `(${report.collisions[0].ref}). Rename the design and re-export.`,
    );
  }
  return parts.length > 0 ? ` ${parts.join(" ")}` : "";
}

/** Where the export ACTUALLY wrote, preferred over what the caller thought was chosen.
 *  Lives here (WS-I I-5, moved from `BatchPanel`) so every save surface — mockups and
 *  transparent overlays — words the destination identically. */
export function destLabel(report: ExportReport, kind: string, believedDir?: string): string {
  if (report.destPath) return `to ${report.destPath}`;
  if (kind !== "desktop") return "as a zip download";
  return believedDir ? `to ${believedDir}` : "to the chosen folder";
}

/** Revealing the folder is best-effort, but a failure must be SAID rather than look like the
 *  app simply choosing not to open it. */
export function openFailureSuffix(report: ExportReport): string {
  return report.openError ? ` (couldn't open the folder: ${report.openError})` : "";
}

/** The shape `hydrateOutputBytes` needs: something written to `savedRoot/localRef` whose
 *  `bytes` may have been dropped. Both `OutputRecord` and a rendered `PrintFileRef` are one. */
type SpillableRecord = { bytes?: Uint8Array; localRef: string; savedRoot?: string };

/**
 * Re-read the bytes of records the memory budget SPILLED after the auto-save wrote them
 * (WS-H2 / AUD-3 F-15 / LB-2).
 *
 * A record with a `savedRoot` and no `bytes` is not lost — it is a file this app wrote,
 * sitting in the run's output folder. Reading it back costs one disk read per record and
 * removes ~1.9 GB of JS heap from a 1000-item run. Records are left UNTOUCHED when the re-read
 * fails or the platform has no `readExportedFile` (web), so `exportBundle`'s existing byte-less
 * `missing` path reports them — never a silently short export.
 *
 * review-1 I-1: generic over the record, because the RENDERED print files spill too. Their path
 * is derived by `printFilesFromOutputs` from the matching mockup, so by the time they reach here
 * a print file carries a real `localRef` and reads back through the very same call — one re-read
 * path for both holders rather than two that can drift.
 */
export async function hydrateOutputBytes<T extends SpillableRecord>(
  io: PlatformIO,
  outputs: T[],
): Promise<T[]> {
  if (!io.readExportedFile) return outputs;
  const out: T[] = [];
  for (const o of outputs) {
    if (o.bytes && o.bytes.length > 0) {
      out.push(o);
      continue;
    }
    if (!o.savedRoot) {
      out.push(o);
      continue;
    }
    const bytes = await io.readExportedFile(o.savedRoot, o.localRef);
    out.push(bytes && bytes.length > 0 ? { ...o, bytes } : o);
  }
  return out;
}

export async function exportBundle(io: PlatformIO, input: BundleInput): Promise<ExportReport> {
  // WS-H2: outputs whose bytes were spilled after the auto-save are re-read from the folder
  // they were written to, BEFORE the bundle is built. A failed re-read stays byte-less and is
  // reported as `missing` below, naming the path it tried. review-1 I-1: the rendered PRINT
  // files spill the same way, so they take the same re-read — otherwise a spilled run ships an
  // empty `Print/` and says "Saved N mockups".
  const outputs = await hydrateOutputBytes(io, input.outputs ?? []);
  /**
   * WS-Y: print files are OFF, so they are dropped HERE — at the one place every export
   * surface funnels through — and not only at the callers. A caller that still holds print
   * refs (a store loaded before the switch flipped, a re-export of an older run) must not be
   * able to put a `Print/` file on disk.
   *
   * Dropped BEFORE the re-read: hydrating them would cost one disk read per design for bytes
   * nothing will write. They are not reported as `missing` either — a file that is
   * deliberately not shipped is not a file that failed to ship, and the panel's count is the
   * MOCK-UP count.
   */
  const printFiles = getExportPrintFiles()
    ? await hydrateOutputBytes(io, input.printFiles ?? [])
    : [];
  const files: { relPath: string; bytes: Uint8Array; mime?: string }[] = [];
  const missing: ExportReport["missing"] = [];
  const collisions: ExportReport["collisions"] = [];
  // Case-INSENSITIVE claim map: APFS and NTFS are both case-insensitive by default, so
  // "Rose.jpg" and "rose.jpg" are one file there and the second write destroys the first.
  // A caller that writes one logical run through SEVERAL calls (a paused conversion's segments)
  // passes its own map so the guard spans them — see `BundleInput.claimed`.
  const claimed = input.claimed ?? new Map<string, string>();
  /** Stage a file, unless its path is already claimed — a duplicate is a silent overwrite. */
  const stage = (relPath: string, bytes: Uint8Array) => {
    const key = relPath.toLowerCase();
    const owner = claimed.get(key);
    if (owner !== undefined) {
      collisions.push({ ref: relPath, reason: `path already claimed by "${owner}" — NOT written (it would overwrite it)` });
      return;
    }
    claimed.set(key, relPath);
    files.push({ relPath, bytes });
  };

  // Iterates the HYDRATED list, never `input.outputs`: reading the raw input here would
  // silently bypass the spill re-read above and ship an export short of the bytes it just
  // wrote to disk. WS-I's optional-`outputs` contract is honoured at the hydrate call
  // instead (`input.outputs ?? []`), which is the one arrangement that satisfies both.
  for (const o of outputs) {
    if (!o.bytes || o.bytes.length === 0) {
      missing.push({
        ref: o.localRef,
        reason: o.savedRoot
          ? `no local bytes for output, and it could not be re-read from ${o.savedRoot}`
          : "no local bytes for output",
      });
      continue;
    }
    stage(o.localRef, o.bytes);
  }
  for (const f of input.files ?? []) {
    if (!f.bytes || f.bytes.length === 0) {
      missing.push({ ref: f.relPath, reason: "no bytes for file" });
      continue;
    }
    stage(f.relPath, f.bytes);
  }
  for (const p of printFiles) {
    if (!p.bytes || p.bytes.length === 0) {
      missing.push({
        ref: p.localRef,
        reason: p.savedRoot
          ? `no local bytes for print file, and it could not be re-read from ${p.savedRoot}`
          : "no local bytes for print file",
      });
      continue;
    }
    stage(p.localRef, p.bytes);
  }
  if (input.sheet) stage(input.sheet.name, input.sheet.bytes);

  // A collision means two distinct designs resolved to ONE output path: the export is
  // incomplete no matter what the write count says, so it belongs in the Error log.
  for (const c of collisions) {
    input.logger?.log("error", `Export · output path collision — ${c.ref}: ${c.reason}`, { ref: c.ref });
  }

  const result = await io.exportBundle({
    name: runBundleName(io.kind === "web" ? "web" : "desktop", input.name, input.runFolder),
    files,
    destDir: input.destDir,
  });
  // Report what the platform ACTUALLY wrote: a cancelled folder dialog used to resolve void
  // and this reported "written: N" with zero bytes on disk ("it says exported, no files").
  if (result && result.cancelled) return { written: 0, missing, failed: [], collisions, cancelled: true };
  // Per-file WRITE failures (disk full, permission denied). The platform has always
  // returned these; dropping them here is why a half-written export read as a success and
  // the operator had no reason to act on — carry the message through AND log it.
  const failed: ExportReport["failed"] = (result?.errors ?? []).map((e) => ({ ref: e.relPath, reason: e.error }));
  for (const f of failed) {
    input.logger?.log("error", `Export · write failed — ${f.ref}: ${f.reason}`, { ref: f.ref });
  }
  return {
    written: result?.written ?? files.length, missing, failed, collisions,
    ...(result?.destPath ? { destPath: result.destPath } : {}),
    ...(result?.openError ? { openError: result.openError } : {}),
  };
}
